import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogComponent, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { catchError, map, of } from 'rxjs';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { ErrorMessageComponent } from '@app/shared/components/error-message/error-message.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { LoggerMeasurementsApiService } from '@app/api/services/logger-measurements-api.service';
import { ILoggerMeasurementSoundSignal } from '@app/api/interfaces/logger-measurement-sound-signal.interface';
import { LeakDetectionSoundSignalService } from '@app/main/leak-detection/services/leak-detection-sound-signal/leak-detection-sound-signal.service';

export interface IDialogSoundSignalRecordingStartTimesData {
  serialNo: string;
  projectId: string;
  loggerMeasurementId: string;
}

interface IRecordingRow {
  index: number;
  id: string;
  url: string;
  date: string;
  time: string;
  milliseconds: string | undefined;
}

@Component({
  selector: 'gc-dialog-sound-signal-recording-start-times',
  templateUrl: './dialog-sound-signal-recording-start-times.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule,
    SvgIconComponent,
    TranslatePipe,
    TranslateDirective,
    NgxPermissionsModule,
    ErrorMessageComponent,
    SpinnerComponent,
  ],
})
export class DialogSoundSignalRecordingStartTimesComponent {
  private ref = inject(DynamicDialogRef);
  private dialogService = inject(DialogService);
  private loggerMeasurementsApiService = inject(LoggerMeasurementsApiService);
  protected soundSignalService = inject(LeakDetectionSoundSignalService);
  protected formattingData = inject(FormattingDataService);

  private instance: DynamicDialogComponent | undefined;

  private data: WritableSignal<IDialogSoundSignalRecordingStartTimesData | undefined> = signal(undefined);

  protected readonly PermissionName = PermissionName;

  public serialNo = computed(() => this.formattingData.formatSerialNo(this.data()?.serialNo));

  private soundSignals: Signal<ILoggerMeasurementSoundSignal[] | null>;

  public loading = computed(() => this.soundSignals() === null);

  public rows = computed<IRecordingRow[]>(() =>
    (this.soundSignals() ?? []).map((soundSignal, i) => this.toRow(soundSignal, i))
  );

  constructor() {
    this.instance = this.dialogService.getInstance(this.ref);
    this.data.set(this.instance.data);

    const dialogData = this.data();

    this.soundSignals = toSignal(
      dialogData
        ? this.loggerMeasurementsApiService.getSoundSignals(dialogData.projectId, dialogData.loggerMeasurementId).pipe(
            map(response => response.data),
            catchError(() => of<ILoggerMeasurementSoundSignal[]>([]))
          )
        : of<ILoggerMeasurementSoundSignal[]>([]),
      { initialValue: null }
    );
  }

  public close(): void {
    this.ref.close();
  }

  private toRow(soundSignal: ILoggerMeasurementSoundSignal, index: number): IRecordingRow {
    const fraction = soundSignal.startTime.match(/\.(\d+)/)?.[1];
    const milliseconds = fraction ? fraction.padEnd(3, '0').slice(0, 3) : undefined;

    return {
      index: index + 1,
      id: soundSignal.id,
      url: soundSignal.url,
      date: this.formattingData.formatTime(soundSignal.startTime, DayJSDateFormat.DDMMYY),
      time: this.formattingData.formatTime(soundSignal.startTime, DayJSDateFormat.LTS),
      milliseconds,
    };
  }
}
